// Tugi Tark AI support-widget mount.
//
// Mirrors the vendored-SDK bootstrap pattern used by `src/insights/index.js`:
// dynamically inject Tugi's remote script, then call
// `window.tugiWidget.initialize({...})` with a `jwtFn` that fetches a signed
// player JWT from OUR backend (the signing secret never leaves the server).
//
// Dormant until `REACT_APP_TUGI_ENABLED=true` AND brand ids are filled, so this
// is inert on live until credentials + the flag are set. See `configs/tugi.js`.
import { useEffect, useRef } from "react";
import api from "services/api";
import Translator from "services/Translator";
import { tugiConfig, isTugiConfigured } from "configs/tugi";

// Single-flight script loader: only ever adds one <script> and re-uses the
// in-flight/resolved promise for every caller (StrictMode double-mount safe).
let scriptPromise = null;
function loadTugiScript(src) {
  if (typeof window !== "undefined" && window.tugiWidget) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-tugi-widget="1"]');
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", reject);
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.crossOrigin = "anonymous";
    script.setAttribute("data-tugi-widget", "1");
    script.onload = () => resolve();
    script.onerror = (err) => {
      scriptPromise = null; // allow a later retry
      reject(err);
    };
    document.body.appendChild(script);
  });
  return scriptPromise;
}

// jwtFn: pull the signed player JWT from our player API. The bearer token is
// attached automatically by services/api (auth.getAccessToken()). Returns null
// on any failure so the widget can fall back to its unauthenticated flow.
async function fetchWidgetJwt() {
  try {
    const res = await api.get(tugiConfig.jwtEndpoint);
    if (res && res.status === 200 && res.data) {
      const token = typeof res.data === "string" ? res.data : res.data.token;
      if (token) return token;
    }
  } catch (err) {
    // Swallow — never break the page because support chat couldn't authenticate.
  }
  return null;
}

export default function TugiWidget({ isAuthenticated }) {
  const initialized = useRef(false);

  useEffect(() => {
    if (!tugiConfig.enabled || !isTugiConfigured()) return undefined;

    // Load for authenticated players (primary case), for anonymous visitors when
    // explicitly enabled, and always after it has already initialised (so login
    // /logout transitions can call refresh()).
    const shouldRun =
      isAuthenticated || tugiConfig.anonymous || initialized.current;
    if (!shouldRun) return undefined;

    let cancelled = false;
    loadTugiScript(tugiConfig.scriptUrl)
      .then(() => {
        if (cancelled || !window.tugiWidget) return;
        if (initialized.current) {
          // Session changed — re-run jwtFn so the widget picks up the new player.
          if (typeof window.tugiWidget.refresh === "function") {
            window.tugiWidget.refresh();
          }
          return;
        }
        const language = Translator.getSelectedLanguage()?.code || "en";
        window.tugiWidget.initialize({
          language,
          jwtFn: fetchWidgetJwt,
          tenantId: tugiConfig.tenantId,
          brandId: tugiConfig.brandId,
          brandName: tugiConfig.brandName,
          httpUrl: tugiConfig.httpUrl,
          wsUrl: tugiConfig.wsUrl,
        });
        initialized.current = true;
      })
      .catch(() => {
        // Script failed to load — leave the rest of the app untouched.
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  return null;
}
